import React from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Badge,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Brightness3 as Brightness3Icon,
  Close as CloseIcon,
  Grade as GradeIcon,
  HelpOutline as HelpOutlineIcon,
  LibraryMusic as LibraryMusicIcon,
  MoveToInbox as MoveToInboxIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  SupervisorAccount as SupervisorAccountIcon,
  WbSunny as WbSunnyIcon,
  AccountBox as AccountBoxIcon,
} from "@material-ui/icons";
import { UserContext } from "../contexts/UserContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { TOKEN_KEY, USER_KEY } from "../CONSTANTS";
import { loggedOutUser } from "../utils/user";
import { START_TOUR_EVENT } from "../utils/tour";
import userAPI from "../restApi/userApi";

const useStyles = makeStyles((theme) => ({
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: "1rem",
    backgroundColor: theme.palette.secondary.main,
  },
  // The menu slides in from the right edge as a side panel (not a dropdown
  // under the avatar): as wide as a comfortable phone sheet, never more than
  // most of a small screen so the page behind stays visible.
  drawerPaper: {
    width: 300,
    maxWidth: "85vw",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 1, 2, 2),
  },
  drawerAvatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    fontSize: "1.4rem",
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
  },
  drawerWho: {
    flex: 1,
    minWidth: 0,
  },
}));

// The settings gear + its menu (theme, admin, account, sign out) -
// extracted out of Navigationbar so it can also be rendered inline
// elsewhere (e.g. Catalog's sticky search row on mobile, where the
// main top bar is hidden). Reads user/theme from context rather than
// props so it drops in anywhere with no plumbing.
export default () => {
  const userContext = React.useContext(UserContext);
  const { themePalette, setThemePalette } = React.useContext(ThemeContext);
  const user = userContext.user;
  const [menuOpen, setMenuOpen] = React.useState(false);
  // Staff only: how many team requests are waiting for review (see
  // PendingTeamRequestCount in api/views.py). Fetched on mount and again
  // each time the menu opens, so the count is fresh when it's read.
  const [pendingRequests, setPendingRequests] = React.useState(0);
  const refreshPendingRequests = React.useCallback(() => {
    if (!user.is_staff) return;
    userAPI
      .getPendingTeamRequestCount()
      .then((res) => setPendingRequests(res.data.count))
      .catch(() => {});
  }, [user.is_staff]);
  React.useEffect(() => {
    refreshPendingRequests();
  }, [refreshPendingRequests]);
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const avatarInitial = (user.first_name || user.email || "?").charAt(0).toUpperCase();
  // Desktop already has a dedicated "Setlist Templates" tab in the main
  // nav (Navigationbar.jsx) - only list it here too on the narrower
  // screens that don't have room for it as its own tab, so it doesn't
  // show up twice.
  const largeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const handleSettingsOpen = () => {
    setMenuOpen(true);
    refreshPendingRequests();
  };

  const handleSettingsClose = () => {
    setMenuOpen(false);
  };

  const toggleThemePalette = () => {
    if (themePalette === "dark") {
      setThemePalette("light");
    } else {
      setThemePalette("dark");
    }
  };

  const handleAdminRedirect = () => {
    // The Django admin site is mounted at /django-admin/ - /admin is
    // taken by this app's own React "Admin" (team management) page.
    window.open("/django-admin/", "_blank");
  };

  const handleTeamRequestsRedirect = () => {
    handleSettingsClose();
    // Approving/rejecting stays in Django admin by design (staff reviews
    // each one by hand) - this just lands on the pending list.
    window.open("/django-admin/users/teamrequest/?status__exact=pending", "_blank");
  };

  const handleSignOut = () => {
    // Revokes the refresh token on the server and clears its cookie. Best
    // effort: signing out locally must work even if this request fails.
    // Plain axios: no Authorization header, because an expired access token
    // would be rejected before the server ever reads the refresh cookie.
    axios.post("/auth/logout/").catch(() => {});
    // Must clear both keys: App.js's initial state reads "user" straight
    // out of localStorage on every fresh page load, so leaving a stale
    // entry there would re-authenticate the user even with no token to
    // back it up.
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    userContext.setUser(loggedOutUser());
  };

  return (
    <>
      <IconButton
        aria-label="Account menu"
        data-tour="account-menu"
        aria-describedby="SettingsButton"
        edge="end"
        onClick={handleSettingsOpen}
        style={{ marginLeft: "auto", outline: "none" }}
      >
        <Badge
          color="error"
          variant="dot"
          overlap="circle"
          invisible={!pendingRequests}
        >
          <Avatar className={classes.avatar}>{avatarInitial}</Avatar>
        </Badge>
      </IconButton>
      <Drawer
        id="SettingsButton"
        anchor="right"
        open={menuOpen}
        onClose={handleSettingsClose}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <Avatar className={classes.drawerAvatar}>{avatarInitial}</Avatar>
          <div className={classes.drawerWho}>
            <Typography variant="subtitle1" noWrap>
              {user.first_name || "Your account"}
            </Typography>
            {user.email && (
              <Typography variant="body2" color="textSecondary" noWrap>
                {user.email}
              </Typography>
            )}
          </div>
          <IconButton aria-label="Close menu" onClick={handleSettingsClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        <List aria-label="Settings list">
          <ListItem
            button
            onClick={() => {
              handleSettingsClose();
              toggleThemePalette();
            }}
          >
            <ListItemIcon>
              {themePalette === "dark" ? <WbSunnyIcon /> : <Brightness3Icon />}
            </ListItemIcon>
            <ListItemText
              primary={
                themePalette === "dark"
                  ? "Enter bright mode"
                  : "Enter dark mode"
              }
            />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              handleSettingsClose();
              window.dispatchEvent(new CustomEvent(START_TOUR_EVENT, { detail: { kind: "member" } }));
            }}
          >
            <ListItemIcon>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="How to use the app" />
          </ListItem>
          {user.is_team_leader && (
            <ListItem
              button
              onClick={() => {
                handleSettingsClose();
                window.dispatchEvent(new CustomEvent(START_TOUR_EVENT, { detail: { kind: "leader" } }));
              }}
            >
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText primary="How to manage the team" />
            </ListItem>
          )}
          {user.is_team_leader && !largeScreen && (
            <ListItem
              button
              component={Link}
              to={{ pathname: "/setlist-templates", state: { from: location } }}
              onClick={handleSettingsClose}
            >
              <ListItemIcon>
                <LibraryMusicIcon />
              </ListItemIcon>
              <ListItemText primary="Setlist Templates" />
            </ListItem>
          )}
          {user.team && (
            <ListItem
              button
              component={Link}
              to="/admin"
              onClick={handleSettingsClose}
            >
              <ListItemIcon>
                <GradeIcon />
              </ListItemIcon>
              <ListItemText primary="Team" />
            </ListItem>
          )}
          {user.is_staff && (
            <ListItem button onClick={handleTeamRequestsRedirect}>
              <ListItemIcon>
                <MoveToInboxIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  pendingRequests
                    ? `Team requests (${pendingRequests})`
                    : "Team requests"
                }
              />
            </ListItem>
          )}
          {user.is_staff && (
            <ListItem button onClick={handleAdminRedirect}>
              <ListItemIcon>
                <GradeIcon />
              </ListItemIcon>
              <ListItemText primary="Django Admin" />
            </ListItem>
          )}
          <ListItem
              button
              component={Link}
              to="/account"
              onClick={() => handleSettingsClose()}
          >
            <ListItemIcon>
              <AccountBoxIcon />
            </ListItemIcon>
            <ListItemText primary="Account" />
          </ListItem>
          <ListItem button onClick={handleSignOut}>
            <ListItemIcon>
              <PowerSettingsNewIcon />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};
